import React, { useCallback, useEffect, useRef, useState } from "react";

/*
 * THE PHOTO CROPPER for "You're meeting". Asked for 2026-09-30: "allow me to
 * zoom in and crop it."
 *
 * Drag to move the photo, the slider (or the wheel) to zoom, and the circle is
 * exactly what the booking page will show. The result is a 256px square JPEG,
 * so what is kept in the booking setting stays small; the original never
 * leaves the browser.
 */

const STAGE = 300;
const OUT = 256;
const MAX_ZOOM = 4;

export default function PhotoCropper({ source, onCancel, onDone }) {
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  useEffect(() => {
    const image = new Image();
    image.onload = () => setImg(image);
    image.src = source;
  }, [source]);

  // Cover the stage at zoom 1, the same way the avatar's circle is filled.
  const base = img ? Math.max(STAGE / img.width, STAGE / img.height) : 1;
  const scale = base * zoom;

  // Keep the photo covering the circle: no empty corners after a drag or zoom.
  const clamp = useCallback(
    (o, s = scale) => {
      if (!img) return o;
      const maxX = Math.max(0, (img.width * s - STAGE) / 2);
      const maxY = Math.max(0, (img.height * s - STAGE) / 2);
      return { x: Math.min(maxX, Math.max(-maxX, o.x)), y: Math.min(maxY, Math.max(-maxY, o.y)) };
    },
    [img, scale]
  );

  const setZoomTo = (z) => {
    const next = Math.min(MAX_ZOOM, Math.max(1, z));
    setZoom(next);
    setOffset((o) => clamp(o, base * next));
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, start: offset };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const d = drag.current;
    setOffset(clamp({ x: d.start.x + (e.clientX - d.x), y: d.start.y + (e.clientY - d.y) }));
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const save = () => {
    if (!img) return;
    // The stage's square, back in the photo's own pixels.
    const visible = STAGE / scale;
    const cx = img.width / 2 - offset.x / scale;
    const cy = img.height / 2 - offset.y / scale;
    const canvas = document.createElement("canvas");
    canvas.width = OUT;
    canvas.height = OUT;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, cx - visible / 2, cy - visible / 2, visible, visible, 0, 0, OUT, OUT);
    onDone(canvas.toDataURL("image/jpeg", 0.88));
  };

  return (
    <div className="crm-bkcrop" role="dialog" aria-modal="true" aria-label="Crop your photo" onClick={onCancel}>
      <div className="crm-bkcrop__box" onClick={(e) => e.stopPropagation()}>
        <h3>Crop your photo</h3>
        <p className="crm-hint">Drag to move it. Zoom in on your face. The circle is what leads see.</p>
        <div
          className="crm-bkcrop__stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={(e) => setZoomTo(zoom - e.deltaY * 0.002)}
        >
          {img ? (
            <img
              src={source}
              alt=""
              draggable={false}
              style={{
                width: img.width * scale,
                height: img.height * scale,
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
              }}
            />
          ) : null}
          <span className="crm-bkcrop__mask" />
        </div>
        <label className="crm-bkcrop__zoom">
          <span className="crm-hint">Zoom</span>
          <input
            type="range"
            min={1}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoomTo(Number(e.target.value))}
            aria-label="Zoom"
          />
        </label>
        <div className="crm-bkcrop__buttons">
          <button type="button" className="crm-btn crm-btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="crm-btn crm-btn--primary" disabled={!img} onClick={save}>
            Use this photo
          </button>
        </div>
      </div>
    </div>
  );
}

/** Read a picked file as a data URL for the cropper, refusing what isn't an image. */
export function readImageFile(file) {
  return new Promise((resolve, reject) => {
    if (!file || !/^image\//.test(file.type)) {
      reject(new Error("That file isn't an image."));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("That image couldn't be read."));
    reader.readAsDataURL(file);
  });
}
